<script lang="ts" context="module">
  import { getAppConfig } from '../../../common/config/app-config';
  import { CONSTANTS } from '../../../common/config/constants';
  import {
    getJoodDayBannerDetails,
    getJoodDayCredits,
    selectCorrectSmartPrice,
    selectCorrectSmartPromoBoolean,
  } from '../../../common/joodUtils';
  import {
    getJoodExpiredMembershipType,
    getJoodMembershipType,
    getStr,
    roundToNDigits,
    type JoodMembershipType,
  } from '../../../common/util';
  import Icon from '../../common/components/Icon.svelte';
  import TimerTag from '../../common/components/tag/TimerTag.svelte';

  const { extraThemeResourcePath, primeLevel, joodExpiredFlag, joodFullCappingLimitReached } = getAppConfig();
  let userJoodMembershipType: JoodMembershipType = getJoodMembershipType();
  const expiredJoodMembershipType = getJoodExpiredMembershipType();
  const { NOPRIME, BASIC, VIP, SMART, BASICPRIME, VIPPRIME, SMARTPRIME } = CONSTANTS;
</script>

<script lang="ts">
  import { getBottomTags } from '../../brand/services/brand-data-services';
  import ProductTileJoodWalletCreditAmount from '../../common/components/productTile/components/ProductTileJoodWalletCreditAmount.svelte';
  import { ProductTilePriceGroupVariantType } from '../../common/components/productTile/product-tile-main';

  export let algoliaProductHit;
  export let bannerType: JoodMembershipType = NOPRIME;
  export let variant: ProductTilePriceGroupVariantType;
  export let backgoundOverride: JoodMembershipType = null;

  if (expiredJoodMembershipType && bannerType === expiredJoodMembershipType) {
    userJoodMembershipType = bannerType = expiredJoodMembershipType;
  }

  const isTacticalVipPromo = algoliaProductHit?.tacticalVipPromo;
  const timerColor = getStr(`extra.joodday.timer.text.color.${userJoodMembershipType}`);
  $: displayPrice = getPrice(bannerType, algoliaProductHit);
  $: wasPrice = getWasPrice(bannerType, algoliaProductHit);
  $: savedPrice = wasPrice - displayPrice;
  $: hasSavings = getHasSavings(bannerType, userJoodMembershipType);

  $: savedPercent = hasSavings && roundToNDigits((savedPrice * 100) / wasPrice, 0);

  $: theme = getJoodDayBannerDetails(backgoundOverride ?? userJoodMembershipType);
  $: creditPoints = getJoodDayCredits(bannerType, algoliaProductHit);
  $: moreCreditText = getMoreCreditText(savedPercent);
  $: visible = getJoodDayBannerVisibility(
    bannerType,
    userJoodMembershipType,
    algoliaProductHit,
    joodFullCappingLimitReached
  );

  const endDateJoodDay = algoliaProductHit?.endDateJoodDay;
  const dealObj = { endDateDeal: endDateJoodDay };
  const bottomTags = { tagsData: getBottomTags(dealObj) };
  const tagData = { product: bottomTags.tagsData[0].product };
  const isJoodDeal = endDateJoodDay ? true : false;
  const joodDayRemainingQuantity = algoliaProductHit?.joodDayRemainingQuantity || 0;

  function getJoodDayBannerVisibility(
    bannerType: JoodMembershipType,
    userJoodMembershipType: JoodMembershipType,
    algoliaHit: any,
    joodFullCappingLimitReached: string
  ) {
    const { tacticalBasicPromo, tacticalBasicPrimePrice, tacticalVipPromo, tacticalVipPrimePrice } = algoliaHit;
    const tacticalSmartPromo = selectCorrectSmartPromoBoolean(
      algoliaHit?.tacticalSmartFirstPromo,
      algoliaHit?.tacticalSmartRepeatingPromo
    );
    const tacticalSmartPrimePrice = selectCorrectSmartPrice(
      algoliaHit?.smartFirstPrimePrice,
      algoliaHit?.smartRepeatingPrimePrice
    );
    const hasTacticalPromo =
      (tacticalBasicPromo && tacticalBasicPrimePrice > 0 && bannerType === 'BASICPRIME') ||
      (tacticalVipPromo && tacticalVipPrimePrice > 0 && bannerType === 'VIPPRIME') ||
      (tacticalSmartPromo && tacticalSmartPrimePrice > 0 && bannerType === 'SMARTPRIME');

    /*
            Rules:
            1. if gray member, hide blue banner
            2. if blue member, hide nothing
            3. if gold member, hide blue banner
        */
    let bannerVisible = true;
    if (userJoodMembershipType === 'NOPRIME' || userJoodMembershipType === 'VIPPRIME') {
      bannerVisible = bannerType !== 'BASICPRIME';
    }

    return hasTacticalPromo && bannerVisible && joodFullCappingLimitReached !== 'true';
  }

  function getMoreCreditText(tacticalPercentageDiscount) {
    let output = '';
    if (tacticalPercentageDiscount) {
      if (userJoodMembershipType === NOPRIME && variant === 'pdp') {
        if (joodDayRemainingQuantity) {
          output = `${getStr('extra.joodday.availble.qty.pdp', [joodDayRemainingQuantity])} ${getStr('extra.jood.wallet.more.credit.pdp.noprime', [tacticalPercentageDiscount])}`;
        } else {
          output = getStr('extra.jood.wallet.more.credit.pdp.noprime', [tacticalPercentageDiscount]);
        }
      } else if (userJoodMembershipType !== NOPRIME && variant === 'pdp') {
        if (joodDayRemainingQuantity) {
          output = `${getStr('extra.joodday.availble.qty.pdp', [joodDayRemainingQuantity])} ${getStr('extra.jood.wallet.more.credit.pdp.prime', [tacticalPercentageDiscount])}`;
        } else {
          output = getStr('extra.jood.wallet.more.credit.pdp.prime', [tacticalPercentageDiscount]);
        }
      } else {
        output = getStr('extra.jood.wallet.more.credit', [tacticalPercentageDiscount]);
      }
    }
    return output;
  }

  function getPrice(bannerType: JoodMembershipType, algoliaProductHit: any) {
    let sourcePrice;
    switch (bannerType) {
      case 'NOPRIME':
        sourcePrice = algoliaProductHit.tacticalVipPrimePrice;
        break;
      case 'BASICPRIME':
        sourcePrice = algoliaProductHit.tacticalBasicPrimePrice;
        break;
      case 'VIPPRIME':
        sourcePrice = algoliaProductHit.tacticalVipPrimePrice;
        break;
      case 'SMARTPRIME':
        sourcePrice = selectCorrectSmartPrice(
          algoliaProductHit.tacticalSmartFirstPrimePrice,
          algoliaProductHit.tacticalSmartRepeatingPrimePrice
        );
        break;
    }

    return sourcePrice;
  }
  function getWasPrice(bannerType: JoodMembershipType, algoliaProductHit: any) {
    let sourcePrice;
    switch (bannerType) {
      case 'NOPRIME':
        sourcePrice = algoliaProductHit.vipPrimePrice;
        break;
      case 'BASICPRIME':
        sourcePrice = algoliaProductHit.basicPrimePrice;
        break;
      case 'VIPPRIME':
        sourcePrice = algoliaProductHit.vipPrimePrice;
        break;
      case 'SMARTPRIME':
        sourcePrice = selectCorrectSmartPrice(
          algoliaProductHit.smartFirstPrimePrice,
          algoliaProductHit.smartRepeatingPrimePrice
        );
        break;
    }

    return sourcePrice;
  }

  function getHasSavings(bannerType: JoodMembershipType, userJoodMembershipType: JoodMembershipType) {
    let visibility = false;
    if (![BASICPRIME, VIPPRIME, SMARTPRIME].includes(userJoodMembershipType as any)) {
      visibility = savedPrice > 0 && bannerType === 'NOPRIME' && !isTacticalVipPromo;
    } else {
      if (joodExpiredFlag === BASIC || joodExpiredFlag === VIP || joodExpiredFlag === SMART) {
        visibility = savedPrice > 0 && bannerType === NOPRIME;
      } else {
        visibility = savedPrice > 0;
      }
    }
    return visibility;
  }
</script>

{#if visible}
  <section
    class="{variant === 'pdp-popup'
      ? 'variant-pdp'
      : ''} c_product-price-jood-wallet-container c_jooddaywalletcard wallet-payday variant-{variant} {primeLevel
      ? 'user-PRIME'
      : 'user-NOPRIME'} jood-{bannerType} {joodExpiredFlag ? 'expired' : ''}"
    style:background-image="url({theme.backgroundImage})"
    style:color={theme.textColor}
    style:border={`1px solid ${theme.borderColor}`}
  >
    <section style:border-bottom={`0.5px solid ${theme.headingBorderColor}`} class="banner-logo">
      <img src={theme.foregroundImage} alt="Jood day logo" />

      {#if isJoodDeal}
        <div class="c_joodtimer" style="color:{timerColor};">
          <Icon type="font" glyph="working-hours" mobileSize={15} desktopSize={18} />
          <section class="c_jooddealtime" style="color:{timerColor}">
            <TimerTag {...tagData} {isJoodDeal} />
          </section>
        </div>
      {:else}
        <div class="jood-day-timer">
          <img src={`${extraThemeResourcePath}/images/jood/extra-calender.svg`} alt="" />
          <span>{getStr('extra.price.today.only.text')}</span>
        </div>
      {/if}
    </section>
    <section class="middle">
      {#if bannerType !== NOPRIME && creditPoints > 0}
        <ProductTileJoodWalletCreditAmount
          isTacticalFlag={true}
          {creditPoints}
          {variant}
          priceType={bannerType}
          {theme}
        />
      {/if}
      {#if moreCreditText}
        <span style:color={theme.moreCashpointTextColor} class="more-credit-note">{@html moreCreditText}</span>
      {/if}
    </section>
  </section>
{/if}

<style lang="scss">
  .variant-plp-grid {
    .middle {
      gap: 4px;
      padding: 4px;
      :global {
        .more-credit-note {
          font-size: 10px;
        }
      }
    }
    &.expired {
      :global {
        .credits-details-left {
          .price,
          .text {
            font-size: 8px;
          }
        }
        .price-expire-card {
          font-size: 7px;
        }
      }
    }
  }
  .middle {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;

    .more-credit-note {
      font-size: 16px;
      line-height: 16px;
      font-weight: 700;
    }
  }
  .variant-plp-list .middle .more-credit-note,
  .variant-pdp .middle .more-credit-note {
    font-size: 16px;
    font-weight: 500;
  }
  :global {
    .variant-product-tile {
      .middle .more-credit-note {
        font-size: 12px !important;
      }
    }
  }
</style>
